import React, { useEffect, useState } from "react";
import { api, formatBg } from "../lib/glucose";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Copy, ArrowsClockwise, ShieldCheck, Eye, EyeSlash, Bell, BellSlash, SpeakerHigh, LinkSimple, Plus, X, Cpu, DownloadSimple, CalendarBlank, Drop, ClockCounterClockwise, CloudArrowDown, PlugsConnected, UploadSimple, Calculator, Warning, Scales } from "@phosphor-icons/react";
import {
  getAlertPrefs, setAlertPrefs, notificationPermission,
  requestNotificationPermission, fireTestAlert,
} from "../lib/alerts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [token, setToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [secretConfigured, setSecretConfigured] = useState(false);
  const [secretInput, setSecretInput] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);
  const [alertPrefs, setAlertPrefsState] = useState(getAlertPrefs());
  const [permission, setPermission] = useState(notificationPermission());

  useEffect(() => {
    if (user) {
      setForm({
        units: user.units,
        low_threshold: user.low_threshold,
        high_threshold: user.high_threshold,
        target_low: user.target_low,
        target_high: user.target_high,
        dia_hours: user.dia_hours ?? 4,
        carb_absorption_hours: user.carb_absorption_hours ?? 3,
      });
    }
    api.get("/settings/api-token").then((r) => setToken(r.data.api_token));
    api.get("/settings/api-secret").then((r) => setSecretConfigured(!!r.data.configured));
  }, [user]);

  if (!form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", form);
      toast.success("Settings saved");
      await refresh();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const regenerate = async () => {
    try {
      const r = await api.post("/settings/api-token");
      setToken(r.data.api_token);
      toast.success("New API token generated");
    } catch { toast.error("Failed to regenerate"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  const saveSecret = async () => {
    if (secretInput.trim().length < 12) {
      toast.error("API Secret must be at least 12 characters");
      return;
    }
    setSavingSecret(true);
    try {
      await api.post("/settings/api-secret", { secret: secretInput.trim() });
      setSecretConfigured(true);
      setSecretInput("");
      toast.success("GlucoCloud API Secret set");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save secret");
    } finally {
      setSavingSecret(false);
    }
  };

  const clearSecret = async () => {
    try {
      await api.delete("/settings/api-secret");
      setSecretConfigured(false);
      toast.success("API Secret cleared");
    } catch {
      toast.error("Failed to clear");
    }
  };

  const updateAlerts = (patch) => {
    const next = setAlertPrefs(patch);
    setAlertPrefsState(next);
  };

  const toggleAlertsEnabled = async (checked) => {
    if (checked) {
      if (typeof Notification === "undefined") {
        toast.error("Your browser doesn't support notifications");
        return;
      }
      const p = await requestNotificationPermission();
      setPermission(p);
      if (p !== "granted") {
        toast.error("Notification permission denied. Enable it in your browser settings.");
        return;
      }
    }
    updateAlerts({ enabled: checked });
    toast.success(checked ? "Browser alerts enabled" : "Browser alerts disabled");
  };

  const testAlert = () => {
    if (permission !== "granted") {
      toast.error("Grant notification permission first");
      return;
    }
    fireTestAlert(user?.units || "mg/dL");
    toast.success("Test alert fired — check your notifications");
  };

  const nsBaseUrl = `${BACKEND}`;
  const uploadUrl = `${BACKEND}/api/v1/entries`;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Preferences</div>
        <h1 className="font-display text-3xl tracking-tight">Settings</h1>
      </div>

      <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="preferences-section">
        <div className="font-display font-medium text-lg">Units & thresholds</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Units</Label>
            <Select value={form.units} onValueChange={(v) => setForm({ ...form, units: v })}>
              <SelectTrigger data-testid="units-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mg/dL">mg/dL</SelectItem>
                <SelectItem value="mmol/L">mmol/L</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">Display only. Values are stored in mg/dL internally.</div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <ThresholdInput label="Urgent Low" value={form.low_threshold} onChange={(v) => setForm({ ...form, low_threshold: v })} testId="alert-low" units={form.units} />
          <ThresholdInput label="Target Low" value={form.target_low} onChange={(v) => setForm({ ...form, target_low: v })} testId="target-low" units={form.units} />
          <ThresholdInput label="Target High" value={form.target_high} onChange={(v) => setForm({ ...form, target_high: v })} testId="target-high" units={form.units} />
          <ThresholdInput label="Urgent High" value={form.high_threshold} onChange={(v) => setForm({ ...form, high_threshold: v })} testId="alert-high" units={form.units} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-border/60">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Duration of Insulin Action</Label>
            <div className="relative">
              <Input
                type="number" step="0.5" min="1" max="8"
                value={form.dia_hours ?? 4}
                onChange={(e) => setForm({ ...form, dia_hours: parseFloat(e.target.value) })}
                data-testid="dia-hours-input"
                className="pr-14 tabular"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">hours</span>
            </div>
            <div className="text-xs text-muted-foreground">Used for IOB. 3-4h typical for rapid-acting insulin.</div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Carb absorption window</Label>
            <div className="relative">
              <Input
                type="number" step="0.5" min="1" max="6"
                value={form.carb_absorption_hours ?? 3}
                onChange={(e) => setForm({ ...form, carb_absorption_hours: parseFloat(e.target.value) })}
                data-testid="carb-hours-input"
                className="pr-14 tabular"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">hours</span>
            </div>
            <div className="text-xs text-muted-foreground">Used for COB. Slow meals may take 3-4h; fast carbs ~1h.</div>
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? "Saving…" : "Save preferences"}
        </Button>
      </section>

      <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="alerts-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              {alertPrefs.enabled ? <Bell size={18} weight="duotone" /> : <BellSlash size={18} weight="duotone" className="text-muted-foreground" />}
              <div className="font-display font-medium text-lg">Browser alerts</div>
              {permission === "granted" && alertPrefs.enabled && (
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full sig-in-range bg-sig-in-range/10" data-testid="alerts-status-active">
                  Active
                </span>
              )}
              {permission === "denied" && (
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full sig-urgent bg-sig-urgent/10">
                  Blocked
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Fires a native browser notification (and optional sound) when your glucose crosses a threshold.
              Runs entirely in this tab — keep GlucoCloud open in the background.
            </p>
          </div>
          <Switch
            checked={alertPrefs.enabled}
            onCheckedChange={toggleAlertsEnabled}
            data-testid="alerts-enabled-switch"
          />
        </div>

        {alertPrefs.enabled && (
          <>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 pt-2 border-t border-border/60">
              <ZoneToggle
                label="Urgent Low"
                sublabel={`< ${form.low_threshold} mg/dL`}
                checked={alertPrefs.onUrgentLow}
                onChange={(v) => updateAlerts({ onUrgentLow: v })}
                testId="alert-zone-urgent-low"
                accent="sig-urgent"
              />
              <ZoneToggle
                label="Urgent High"
                sublabel={`> ${form.high_threshold} mg/dL`}
                checked={alertPrefs.onUrgentHigh}
                onChange={(v) => updateAlerts({ onUrgentHigh: v })}
                testId="alert-zone-urgent-high"
                accent="sig-high"
              />
              <ZoneToggle
                label="Low (below target)"
                sublabel={`${form.low_threshold}–${form.target_low} mg/dL`}
                checked={alertPrefs.onLow}
                onChange={(v) => updateAlerts({ onLow: v })}
                testId="alert-zone-low"
              />
              <ZoneToggle
                label="High (above target)"
                sublabel={`${form.target_high}–${form.high_threshold} mg/dL`}
                checked={alertPrefs.onHigh}
                onChange={(v) => updateAlerts({ onHigh: v })}
                testId="alert-zone-high"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6 pt-2 border-t border-border/60">
              <div className="space-y-1.5">
                <Label>Repeat cooldown</Label>
                <Select
                  value={String(alertPrefs.cooldownMin)}
                  onValueChange={(v) => updateAlerts({ cooldownMin: parseInt(v, 10) })}
                >
                  <SelectTrigger data-testid="alerts-cooldown-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="10">10 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-muted-foreground">
                  Won&apos;t re-fire the same zone within this window.
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 rounded-md border border-border/60 px-3 h-[62px] mt-[22px]">
                <div className="flex items-center gap-2">
                  <SpeakerHigh size={16} />
                  <div>
                    <div className="text-sm font-medium">Sound</div>
                    <div className="text-xs text-muted-foreground">Play a short beep on alerts</div>
                  </div>
                </div>
                <Switch
                  checked={alertPrefs.sound}
                  onCheckedChange={(v) => updateAlerts({ sound: v })}
                  data-testid="alerts-sound-switch"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border/60">
              <div className="text-xs text-muted-foreground">
                Tip: browsers throttle background tab notifications. Pin this tab for reliable alerts.
              </div>
              <Button variant="outline" size="sm" onClick={testAlert} data-testid="alerts-test-btn" className="gap-1.5">
                <Bell size={14} /> Test alert
              </Button>
            </div>
          </>
        )}

        {permission === "denied" && (
          <div className="text-xs sig-urgent">
            Notifications are blocked in your browser. Click the lock icon in the address bar → Site settings → Allow Notifications, then reload.
          </div>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-6 space-y-4" data-testid="ns-secret-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" />
              <div className="font-display font-medium text-lg">GlucoCloud API Secret</div>
              {secretConfigured && (
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full sig-in-range bg-sig-in-range/10" data-testid="secret-status-configured">
                  Configured
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Use this secret in any classic CGM uploader (xDrip+, Vitatok, Spike, AndroidAPS, Loop).
              They&apos;ll send it as a SHA-1 hash in the <code className="font-mono text-xs">api-secret</code> header.
              Must be at least 12 characters.
            </p>
          </div>
          {secretConfigured && (
            <Button variant="ghost" onClick={clearSecret} data-testid="clear-secret-btn" className="text-xs text-muted-foreground">
              Clear
            </Button>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
              {secretConfigured ? "Change API Secret" : "Set API Secret"}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={secretInput}
                  onChange={(e) => setSecretInput(e.target.value)}
                  placeholder="A long random passphrase (min. 12 chars)"
                  className="font-mono text-sm pr-9"
                  data-testid="ns-secret-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-secret-visibility"
                  aria-label="toggle visibility"
                >
                  {showSecret ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <Button onClick={saveSecret} disabled={savingSecret || secretInput.length < 12} data-testid="save-secret-btn">
                {savingSecret ? "Saving…" : "Save secret"}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1.5 tabular">
              {secretInput.length > 0 && (
                <>
                  {secretInput.length} / min 12 characters
                  {secretInput.length < 12 && <span className="sig-urgent"> · too short</span>}
                </>
              )}
            </div>
          </div>

          <div className="pt-2 border-t border-border/60 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Uploader configuration</div>
            <div className="grid gap-2">
              <div>
                <div className="text-xs text-muted-foreground mb-1">GlucoCloud URL</div>
                <div className="flex gap-2">
                  <Input readOnly value={nsBaseUrl} className="font-mono text-sm" data-testid="ns-base-url" />
                  <Button variant="outline" size="icon" onClick={() => copy(nsBaseUrl)}><Copy size={14} /></Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                In xDrip+ → <em>Settings → Cloud Upload → Cloud Sync (REST-API)</em>:
                paste the URL above into <strong>Base URL</strong> and the plaintext secret you set into <strong>API Secret</strong>.
                In Vitatok / Diabox / Spike, use the same fields.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card p-6 space-y-4" data-testid="api-token-section">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-medium text-lg">Alternative: Bearer token</div>
            <p className="text-sm text-muted-foreground mt-1">
              If your uploader doesn&apos;t support the <code className="font-mono text-xs">api-secret</code> header,
              use this token via <code className="font-mono text-xs">Authorization: Bearer</code> or <code className="font-mono text-xs">?token=</code>.
            </p>
          </div>
          <Button variant="outline" onClick={regenerate} data-testid="regen-token-btn" className="gap-1">
            <ArrowsClockwise size={14} /> {token ? "Regenerate" : "Generate"}
          </Button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">API Token</div>
            <div className="flex gap-2">
              <Input readOnly value={token || "Not generated yet"} className="font-mono text-sm" data-testid="api-token-value" />
              <Button variant="outline" size="icon" onClick={() => token && copy(token)} disabled={!token}><Copy size={14} /></Button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Upload endpoint</div>
            <div className="flex gap-2">
              <Input readOnly value={uploadUrl} className="font-mono text-sm" data-testid="upload-url" />
              <Button variant="outline" size="icon" onClick={() => copy(uploadUrl)}><Copy size={14} /></Button>
            </div>
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Example curl</summary>
            <pre className="mt-2 p-3 rounded-md bg-secondary text-xs overflow-x-auto font-mono">{`curl -X POST ${uploadUrl} \\
  -H "Authorization: Bearer ${token || "<YOUR_TOKEN>"}" \\
  -H "Content-Type: application/json" \\
  -d '[{"sgv":128,"direction":"Flat","date":${Date.now()}}]'`}</pre>
          </details>
        </div>
      </section>

      <ShareLinksSection />
      <BasalProfileSection user={user} refresh={refresh} />
      <BolusCalcSection user={user} refresh={refresh} />
      <SensorSection />
      <DexcomShareSection />
      <ImportSection />
      <ExportSection />
    </div>
  );
}

function ThresholdInput({ label, value, onChange, testId, units }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          data-testid={testId}
          className="pr-14 tabular"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">mg/dL</span>
      </div>
      {units === "mmol/L" && (
        <div className="text-xs text-muted-foreground tabular">≈ {formatBg(value, "mmol/L")} mmol/L</div>
      )}
    </div>
  );
}

function ZoneToggle({ label, sublabel, checked, onChange, testId, accent }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2.5">
      <div className="min-w-0">
        <div className={`text-sm font-medium ${accent || ""}`}>{label}</div>
        <div className="text-xs text-muted-foreground tabular">{sublabel}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

// ============ Share Links ============

function ShareLinksSection() {
  const [links, setLinks] = useState([]);
  const [label, setLabel] = useState("");
  const [ttl, setTtl] = useState("30");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/share").then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/share", { label: label.trim() || null, ttl_days: parseInt(ttl, 10) });
      setLabel("");
      toast.success("Share link created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    } finally { setBusy(false); }
  };

  const revoke = async (slug) => {
    if (!window.confirm("Revoke this link? It can't be undone.")) return;
    try {
      await api.delete(`/share/${slug}`);
      toast.success("Link revoked");
      load();
    } catch { toast.error("Failed to revoke"); }
  };

  const shareUrl = (slug) => `${window.location.origin}/share/${slug}`;
  const copyLink = (slug) => {
    navigator.clipboard.writeText(shareUrl(slug));
    toast.success("Link copied");
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="share-links-section">
      <div className="flex items-center gap-2">
        <LinkSimple size={18} weight="duotone" />
        <div className="font-display font-medium text-lg">Share links</div>
      </div>
      <p className="text-sm text-muted-foreground max-w-lg">
        Create read-only links to share your live glucose with family, doctors, or
        school without giving them an account. Anyone with the link can view — revoke anytime.
      </p>

      <div className="grid sm:grid-cols-[1fr_140px_auto] gap-2">
        <Input
          placeholder="Label (e.g. Dr. Patel, School nurse)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          data-testid="share-label-input"
        />
        <Select value={ttl} onValueChange={setTtl}>
          <SelectTrigger data-testid="share-ttl-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 day</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="365">1 year</SelectItem>
            <SelectItem value="0">Never expire</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={create} disabled={busy} data-testid="share-create-btn" className="gap-1.5">
          <Plus size={14} /> Create link
        </Button>
      </div>

      {links.length > 0 ? (
        <ul className="space-y-2 pt-2 border-t border-border/60" data-testid="share-links-list">
          {links.filter((l) => !l.revoked).map((l) => (
            <li key={l.slug} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{l.label || "Untitled link"}</div>
                <div className="text-xs text-muted-foreground font-mono truncate" data-testid={`share-slug-${l.slug}`}>
                  {shareUrl(l.slug)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Created {new Date(l.created_at).toLocaleDateString()}
                  {l.expires_at ? ` · Expires ${new Date(l.expires_at).toLocaleDateString()}` : " · Never expires"}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => copyLink(l.slug)} data-testid={`share-copy-${l.slug}`} className="gap-1">
                <Copy size={12} /> Copy
              </Button>
              <Button variant="ghost" size="sm" onClick={() => revoke(l.slug)} data-testid={`share-revoke-${l.slug}`} className="text-muted-foreground hover:text-destructive gap-1">
                <X size={12} /> Revoke
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-muted-foreground pt-2 border-t border-border/60">No links yet.</div>
      )}
    </section>
  );
}

// ============ Sensor sessions (Dexcom G6/G7, Libre) ============

function SensorSection() {
  const [types, setTypes] = useState([]);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [dtype, setDtype] = useState("dexcom_g7");
  const [serial, setSerial] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, c, h] = await Promise.all([
        api.get("/sensors/types"),
        api.get("/sensors/current"),
        api.get("/sensors", { params: { limit: 10 } }),
      ]);
      setTypes(t.data);
      setCurrent(c.data);
      setHistory(h.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/sensors", { device_type: dtype, serial: serial.trim() || null });
      setSerial("");
      toast.success("Sensor started");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start sensor");
    } finally { setBusy(false); }
  };

  const endCurrent = async () => {
    if (!current) return;
    if (!window.confirm("End this sensor session?")) return;
    try {
      await api.post(`/sensors/${current.id}/end`);
      toast.success("Sensor ended");
      load();
    } catch { toast.error("Failed to end sensor"); }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="sensor-section">
      <div className="flex items-center gap-2">
        <Cpu size={18} weight="duotone" />
        <div className="font-display font-medium text-lg">Sensor sessions</div>
      </div>

      {current ? (
        <div className="p-4 rounded-md border border-border/60 bg-secondary/40 space-y-2" data-testid="sensor-current-card">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Currently active</div>
              <div className="font-display text-lg mt-0.5">{current.device_label}</div>
              {current.serial && <div className="text-xs text-muted-foreground font-mono">SN {current.serial}</div>}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Day</div>
              <div className={`font-display text-3xl tabular ${current.expired ? "sig-urgent" : ""}`} data-testid="sensor-day-badge">
                {current.day}<span className="text-base text-muted-foreground">/{current.expected_days}</span>
              </div>
            </div>
          </div>
          <div className="w-full h-1.5 rounded-full bg-border/60 overflow-hidden">
            <div
              className="h-full bg-foreground transition-all"
              style={{ width: `${Math.min(100, (current.day / current.expected_days) * 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Started {new Date(current.started_at).toLocaleString()}</span>
            <button onClick={endCurrent} className="text-muted-foreground hover:text-destructive" data-testid="sensor-end-btn">
              End session
            </button>
          </div>
          {current.expired && (
            <div className="text-xs sig-urgent">This sensor has passed its expected duration — expect end-of-life warnings.</div>
          )}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">No active sensor. Start one below.</div>
      )}

      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 pt-2 border-t border-border/60">
        <Select value={dtype} onValueChange={setDtype}>
          <SelectTrigger data-testid="sensor-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {types.map((t) => (
              <SelectItem key={t.key} value={t.key}>{t.label} ({t.days} days)</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Serial (optional)"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          data-testid="sensor-serial-input"
          className="font-mono text-sm"
        />
        <Button onClick={start} disabled={busy} data-testid="sensor-start-btn" className="gap-1.5">
          <Plus size={14} /> Start
        </Button>
      </div>

      {history.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground text-xs uppercase tracking-[0.18em]" data-testid="sensor-history-toggle">
            History ({history.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {history.map((h) => (
              <li key={h.id} className="flex justify-between py-1 border-b border-border/40 last:border-0">
                <span>{h.device_label}{h.serial ? ` · ${h.serial}` : ""}</span>
                <span className="text-muted-foreground tabular">
                  {new Date(h.started_at).toLocaleDateString()}
                  {h.ended_at ? ` → ${new Date(h.ended_at).toLocaleDateString()}` : " · active"}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}


// ============ CSV Export ============

function ExportSection() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);

  const build = (kind) => {
    // Convert local date strings to epoch ms (inclusive of `to` end-of-day).
    const since = new Date(`${from}T00:00:00`).getTime();
    const until = new Date(`${to}T23:59:59.999`).getTime();
    const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
    return `${base}/api/export/${kind}.csv?since=${since}&until=${until}`;
  };

  const download = async (kind) => {
    try {
      const r = await api.get(`/export/${kind}.csv`, {
        params: {
          since: new Date(`${from}T00:00:00`).getTime(),
          until: new Date(`${to}T23:59:59.999`).getTime(),
        },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `glucocloud-${kind}-${from}-to-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind} CSV downloaded`);
    } catch (e) {
      toast.error(`Failed to export ${kind}`);
    }
  };

  const setPreset = (days) => {
    const d = new Date(Date.now() - days * 86400_000).toISOString().slice(0, 10);
    setFrom(d);
    setTo(today);
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="export-section">
      <div className="flex items-center gap-2">
        <DownloadSimple size={18} weight="duotone" />
        <div className="font-display font-medium text-lg">Export data</div>
      </div>
      <p className="text-sm text-muted-foreground max-w-lg">
        Download your glucose readings and treatments as CSV. Perfect for handing to
        your endocrinologist, importing into Excel/Google Sheets, or feeding into
        research tools.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>From</Label>
          <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} data-testid="export-from-date" />
        </div>
        <div className="space-y-1.5">
          <Label>To</Label>
          <Input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} data-testid="export-to-date" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 -mt-1">
        <button onClick={() => setPreset(7)} className="text-xs px-2.5 py-1 rounded-md border border-border/60 text-muted-foreground hover:text-foreground" data-testid="export-preset-7">Last 7 days</button>
        <button onClick={() => setPreset(30)} className="text-xs px-2.5 py-1 rounded-md border border-border/60 text-muted-foreground hover:text-foreground" data-testid="export-preset-30">Last 30 days</button>
        <button onClick={() => setPreset(90)} className="text-xs px-2.5 py-1 rounded-md border border-border/60 text-muted-foreground hover:text-foreground" data-testid="export-preset-90">Last 90 days</button>
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60">
        <Button onClick={() => download("entries")} data-testid="export-entries-btn" className="gap-1.5">
          <DownloadSimple size={14} /> Glucose entries
        </Button>
        <Button variant="outline" onClick={() => download("treatments")} data-testid="export-treatments-btn" className="gap-1.5">
          <DownloadSimple size={14} /> Treatments
        </Button>
      </div>
    </section>
  );
}

// ============ Basal Profile ============

function BasalProfileSection({ user, refresh }) {
  const [rates, setRates] = useState(() => user?.basal_profile ?? Array(24).fill(0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRates(user?.basal_profile ?? Array(24).fill(0));
  }, [user?.basal_profile]);

  const totalDaily = rates.reduce((a, b) => a + (parseFloat(b) || 0), 0);

  const setHour = (h, v) => {
    const next = [...rates];
    next[h] = v === "" ? 0 : parseFloat(v);
    setRates(next);
  };

  const flatFill = () => {
    const perHour = window.prompt("Fill every hour with this basal rate (U/hr):", "0.6");
    if (perHour == null) return;
    const v = parseFloat(perHour);
    if (Number.isNaN(v) || v < 0) return;
    setRates(Array(24).fill(v));
  };

  const clear = () => setRates(Array(24).fill(0));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { basal_profile: rates.map((r) => parseFloat(r) || 0) });
      toast.success("Basal profile saved");
      refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const has = rates.some((r) => parseFloat(r) > 0);

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="basal-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Drop size={18} weight="duotone" />
          <div className="font-display font-medium text-lg">Basal profile</div>
        </div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground tabular">
          Total daily: <span className="text-foreground font-medium" data-testid="basal-total">{totalDaily.toFixed(2)} U</span>
        </div>
      </div>
      <p className="text-sm text-muted-foreground max-w-lg">
        Your hourly long-acting insulin (basal) schedule in units per hour. Not applied
        to IOB math yet — this is a reference profile you can share with your endo
        and use for future pump/loop integrations.
      </p>

      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 pt-2 border-t border-border/60">
        {rates.map((r, h) => (
          <div key={h} className="space-y-0.5">
            <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground tabular">
              {String(h).padStart(2, "0")}:00
            </div>
            <Input
              type="number"
              step="0.05"
              min="0"
              max="10"
              value={r}
              onChange={(e) => setHour(h, e.target.value)}
              data-testid={`basal-hour-${h}`}
              className="h-8 text-xs tabular px-1.5"
            />
          </div>
        ))}
      </div>

      {has && (
        <div className="h-10 flex items-end gap-[2px]" aria-hidden="true">
          {rates.map((r, h) => {
            const max = Math.max(...rates.map((x) => parseFloat(x) || 0), 0.01);
            const pct = Math.max(2, ((parseFloat(r) || 0) / max) * 100);
            return (
              <div
                key={h}
                className="flex-1 bg-foreground/80 rounded-sm"
                style={{ height: `${pct}%` }}
                title={`${String(h).padStart(2, "0")}:00 · ${r} U/hr`}
              />
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60">
        <Button onClick={save} disabled={saving} data-testid="basal-save-btn">
          {saving ? "Saving…" : "Save profile"}
        </Button>
        <Button variant="outline" onClick={flatFill} data-testid="basal-flat-btn" className="gap-1.5">
          <ClockCounterClockwise size={14} /> Fill flat
        </Button>
        <Button variant="ghost" onClick={clear} data-testid="basal-clear-btn" className="text-muted-foreground hover:text-destructive">
          Clear
        </Button>
      </div>
    </section>
  );
}


// ============ Dexcom Share bridge ============

function DexcomShareSection() {
  const [status, setStatus] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [region, setRegion] = useState("us");
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const load = () => api.get("/integrations/dexcom-share").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!username.trim() || !password) {
      toast.error("Enter your Dexcom Share username and password");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/integrations/dexcom-share", { username: username.trim(), password, region });
      toast.success(`Connected · ${r.data.immediate_inserted} reading${r.data.immediate_inserted === 1 ? "" : "s"} synced`);
      setPassword("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect");
    } finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/dexcom-share/sync");
      toast.success(`Synced · ${r.data.inserted} new reading${r.data.inserted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Dexcom Share? Your existing readings are kept; new ones will stop syncing.")) return;
    try {
      await api.delete("/integrations/dexcom-share");
      toast.success("Disconnected");
      setUsername("");
      setPassword("");
      load();
    } catch { toast.error("Failed to disconnect"); }
  };

  const connected = status?.enabled;

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="dexcom-share-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CloudArrowDown size={18} weight="duotone" />
            <div className="font-display font-medium text-lg">Dexcom Share bridge</div>
            {connected && (
              <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full sig-in-range bg-sig-in-range/10 flex items-center gap-1" data-testid="dexcom-connected-badge">
                <PlugsConnected size={11} /> Connected
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">
            Auto-import readings from your Dexcom account every 5 minutes — no
            xDrip+, no phone-side uploader needed. Uses the same read-only Share
            API that Sugarmate and Nightscout use.
          </p>
        </div>
      </div>

      {connected ? (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3 text-sm border-t border-border/60 pt-4">
            <Info label="Account" value={status.username} testId="dexcom-username-display" />
            <Info label="Region" value={{ us: "United States", ous: "Rest of world", jp: "Japan" }[status.region] || status.region} />
            <Info
              label="Last poll"
              value={status.last_poll_at ? new Date(status.last_poll_at).toLocaleString() : "Not yet"}
              testId="dexcom-last-poll"
            />
          </div>
          {status.last_error && (
            <div className="text-xs sig-urgent" data-testid="dexcom-last-error">
              Last error: {status.last_error}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={syncNow} disabled={busy} data-testid="dexcom-sync-btn" className="gap-1.5">
              <ArrowsClockwise size={14} /> Sync now
            </Button>
            <Button variant="ghost" onClick={disconnect} className="text-muted-foreground hover:text-destructive" data-testid="dexcom-disconnect-btn">
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-border/60">
            <div className="space-y-1.5">
              <Label>Dexcom username</Label>
              <Input
                type="text"
                autoComplete="off"
                placeholder="you@example.com"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="dexcom-username-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Dexcom password</Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="dexcom-password-input"
                  className="pr-9"
                />
                <button
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Show password"
                  data-testid="dexcom-password-toggle"
                >
                  {showPwd ? <EyeSlash size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Region</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="dexcom-region-select" className="sm:max-w-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="us">United States (share2.dexcom.com)</SelectItem>
                <SelectItem value="ous">Rest of world (shareous1.dexcom.com)</SelectItem>
                <SelectItem value="jp">Japan (share.dexcom.jp)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-muted-foreground border-t border-border/60 pt-3">
            <strong className="text-foreground">Prep:</strong> in the Dexcom app, go to
            Settings → Share and add a follower named <span className="font-mono">glucocloud</span>{" "}
            (any name works). Use your primary Dexcom account credentials below —
            we encrypt them at rest and never share them.
          </div>
          <Button onClick={connect} disabled={busy} data-testid="dexcom-connect-btn" className="gap-1.5">
            <PlugsConnected size={14} /> {busy ? "Connecting…" : "Connect Dexcom"}
          </Button>
        </>
      )}
    </section>
  );
}

function Info({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="text-sm font-medium mt-0.5 truncate" data-testid={testId}>{value || "—"}</div>
    </div>
  );
}


// ============ Bolus Calculator ============

function BolusCalcSection({ user, refresh }) {
  const [defaults, setDefaults] = useState(null);
  const [weight, setWeight] = useState(user?.weight_kg ?? "");
  const [tdd, setTdd] = useState(user?.tdd_units ?? "");
  const [icr, setIcr] = useState(user?.icr ?? "");
  const [isf, setIsf] = useState(user?.isf ?? "");
  const [target, setTarget] = useState(user?.bolus_target ?? "");
  const [carbs, setCarbs] = useState("");
  const [bg, setBg] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadDefaults = () => api.get("/dosing/defaults").then((r) => setDefaults(r.data)).catch(() => {});
  useEffect(() => { loadDefaults(); }, []);
  useEffect(() => {
    setWeight(user?.weight_kg ?? "");
    setTdd(user?.tdd_units ?? "");
    setIcr(user?.icr ?? "");
    setIsf(user?.isf ?? "");
    setTarget(user?.bolus_target ?? "");
  }, [user?.weight_kg, user?.tdd_units, user?.icr, user?.isf, user?.bolus_target]);

  const savePrefs = async () => {
    setSaving(true);
    try {
      const patch = {
        weight_kg: weight === "" ? null : parseFloat(weight),
        tdd_units: tdd === "" ? null : parseFloat(tdd),
        icr: icr === "" ? null : parseFloat(icr),
        isf: isf === "" ? null : parseFloat(isf),
        bolus_target: target === "" ? null : parseFloat(target),
      };
      await api.patch("/settings", patch);
      toast.success("Dosing preferences saved");
      refresh?.();
      loadDefaults();
    } catch (e) {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const applySuggested = () => {
    if (!defaults?.suggested) return;
    const s = defaults.suggested;
    if (s.tdd_units) setTdd(String(s.tdd_units));
    if (s.icr) setIcr(String(s.icr));
    if (s.isf) setIsf(String(s.isf));
    if (s.bolus_target) setTarget(String(s.bolus_target));
    toast.success("Applied weight-based suggestions");
  };

  const calculate = async () => {
    try {
      const body = { carbs: parseFloat(carbs) || 0 };
      if (bg !== "") body.current_bg = parseFloat(bg);
      const r = await api.post("/dosing/bolus-calc", body);
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Calculation failed");
    }
  };

  const hasProfile = icr && isf;

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="bolus-section">
      <div className="flex items-center gap-2">
        <Calculator size={18} weight="duotone" />
        <div className="font-display font-medium text-lg">Bolus calculator</div>
      </div>
      <p className="text-sm text-muted-foreground max-w-lg">
        Not medical advice. This is a first-pass estimate using standard "rule of 500 / 1800"
        heuristics. Always cross-check with your endo before dosing.
      </p>

      {/* Profile constants */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2 border-t border-border/60">
        <NumberInput label="Weight" sublabel="kg" value={weight} onChange={setWeight} testId="dose-weight-input" min="20" max="250" step="0.5" />
        <NumberInput label="TDD" sublabel="U/day" value={tdd} onChange={setTdd} testId="dose-tdd-input" min="1" max="200" step="0.5" />
        <NumberInput label="ICR" sublabel="g/U" value={icr} onChange={setIcr} testId="dose-icr-input" min="1" max="50" step="0.5" />
        <NumberInput label="ISF" sublabel="mg/dL/U" value={isf} onChange={setIsf} testId="dose-isf-input" min="10" max="200" step="1" />
        <NumberInput label="Target BG" sublabel="mg/dL" value={target} onChange={setTarget} testId="dose-target-input" min="60" max="200" step="1" />
      </div>

      {defaults?.suggested?.icr && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Scales size={13} />
          Suggested (from {defaults.weight_kg || "weight"} kg): TDD {defaults.suggested.tdd_units}U, ICR {defaults.suggested.icr}g/U, ISF {defaults.suggested.isf}mg/dL/U.
          <button onClick={applySuggested} className="underline hover:text-foreground" data-testid="dose-apply-suggested">Apply</button>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={savePrefs} disabled={saving} data-testid="dose-save-btn" size="sm">
          {saving ? "Saving…" : "Save dosing prefs"}
        </Button>
      </div>

      {/* Calculator */}
      <div className="grid sm:grid-cols-3 gap-3 pt-4 border-t border-border/60 items-end">
        <NumberInput label="Carbs" sublabel="grams" value={carbs} onChange={setCarbs} testId="calc-carbs-input" min="0" max="500" step="1" />
        <NumberInput label="Current BG" sublabel="mg/dL (auto)" value={bg} onChange={setBg} testId="calc-bg-input" min="20" max="600" step="1" />
        <Button onClick={calculate} disabled={!hasProfile && !defaults?.suggested?.icr} data-testid="calc-run-btn" className="gap-1.5">
          <Calculator size={14} /> Calculate bolus
        </Button>
      </div>

      {result && (
        <div className="rounded-md border border-border/60 bg-secondary/40 p-4 space-y-3" data-testid="calc-result">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Suggested dose</div>
            <div className="font-display text-4xl tabular" data-testid="calc-suggested">
              {result.suggested_units}<span className="text-lg text-muted-foreground ml-1">U</span>
            </div>
          </div>
          <div className="grid grid-cols-3 text-xs text-muted-foreground pt-2 border-t border-border/40">
            <div>Food<br /><span className="text-foreground tabular">{result.breakdown.food} U</span></div>
            <div>Correction<br /><span className="text-foreground tabular">{result.breakdown.correction} U</span></div>
            <div>− IOB<br /><span className="text-foreground tabular">{result.breakdown.iob_now} U</span></div>
          </div>
          {result.warnings?.length > 0 && (
            <ul className="space-y-1 pt-2 border-t border-border/40" data-testid="calc-warnings">
              {result.warnings.map((w, i) => (
                <li key={i} className="text-xs sig-urgent flex items-start gap-1.5">
                  <Warning size={12} className="mt-0.5 flex-none" /> {w}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function NumberInput({ label, sublabel, value, onChange, testId, ...inputProps }) {
  return (
    <div className="space-y-1">
      <Label>
        {label} <span className="text-[10px] text-muted-foreground normal-case">{sublabel}</span>
      </Label>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="tabular"
        {...inputProps}
      />
    </div>
  );
}

// ============ Import from Apple Health / CSV ============

function ImportSection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (endpoint, file, extra = "") => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(endpoint + extra, form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data?.inserted?.glucose ?? r.data?.inserted ?? 0} readings`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-6 space-y-5" data-testid="import-section">
      <div className="flex items-center gap-2">
        <UploadSimple size={18} weight="duotone" />
        <div className="font-display font-medium text-lg">Import from Apple Health / CSV</div>
      </div>
      <p className="text-sm text-muted-foreground max-w-lg">
        Bring in historical readings. On iPhone: Health app → profile → Export All Health Data,
        AirDrop the zip here. Or drop in a CSV from Google Takeout, LibreView, or any tool
        with a datetime + glucose column.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-border/60">
        <FileImportBox
          label="Apple Health"
          hint="export.zip"
          accept=".zip"
          testId="import-apple"
          disabled={busy}
          onChange={(f) => upload("/import/apple-health", f)}
        />
        <FileImportBox
          label="CSV — glucose"
          hint="sgv or value column"
          accept=".csv"
          testId="import-csv-entries"
          disabled={busy}
          onChange={(f) => upload("/import/csv", f, "?kind=entries")}
        />
        <FileImportBox
          label="CSV — treatments"
          hint="insulin / carbs / notes"
          accept=".csv"
          testId="import-csv-treatments"
          disabled={busy}
          onChange={(f) => upload("/import/csv", f, "?kind=treatments")}
        />
      </div>

      {result && (
        <div className="text-xs text-muted-foreground pt-2 border-t border-border/60" data-testid="import-result">
          {result.parsed?.glucose != null && (
            <>Parsed {result.parsed.glucose} glucose + {result.parsed.treatments} treatments · Inserted {result.inserted.glucose} + {result.inserted.treatments} new{result.parsed.skipped ? ` · Skipped ${result.parsed.skipped}` : ""}.</>
          )}
          {result.parsed?.glucose == null && (
            <>Parsed {result.parsed} rows · Inserted {result.inserted} new{result.skipped ? ` · Skipped ${result.skipped}` : ""}.</>
          )}
        </div>
      )}
    </section>
  );
}

function FileImportBox({ label, hint, accept, testId, disabled, onChange }) {
  const [file, setFile] = useState(null);
  return (
    <label className={`block rounded-md border border-dashed border-border/60 p-4 text-center cursor-pointer hover:border-foreground/50 transition-colors ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
      <UploadSimple size={20} className="mx-auto text-muted-foreground" />
      <div className="text-sm font-medium mt-2">{label}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
      {file && <div className="text-[10px] text-foreground mt-1 truncate">{file.name}</div>}
      <input
        type="file"
        accept={accept}
        className="hidden"
        data-testid={testId}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) { setFile(f); onChange(f); e.target.value = ""; }
        }}
      />
    </label>
  );
}

